import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Inicio' },
    { path: '/empresa', label: 'Empresa' },
    { path: '/servicios', label: 'Servicios' },
    { path: '/faq', label: 'FAQ' },
    { path: '/contacto', label: 'Contacto' },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-[#ff0000] rounded-lg flex items-center justify-center">
                  <i className="ri-fire-line text-white text-2xl"></i>
                </div>
                <div className="flex flex-col">
                  <span className={`text-lg font-bold leading-tight ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                    JUSTCLEAN
                  </span>
                  <span className="text-xs font-semibold text-[#ff0000] leading-tight">
                    INCENDIOS
                  </span>
                </div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors whitespace-nowrap ${
                    location.pathname === link.path
                      ? 'text-[#ff0000]'
                      : isScrolled
                      ? 'text-gray-700 hover:text-[#ff0000]'
                      : 'text-white hover:text-[#ff0000]'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center space-x-3">
              <a
                href="tel:+34602365996"
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${
                  isScrolled
                    ? 'text-gray-700 hover:bg-gray-100'
                    : 'text-white hover:bg-white/10'
                }`}
              >
                <i className="ri-phone-line mr-2"></i>
                602 36 59 96
              </a>
              <a
                href="https://wa.me/34602365996"
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-2.5 bg-[#ff0000] text-white text-sm font-semibold rounded-lg hover:bg-[#cc0000] transition-all whitespace-nowrap"
              >
                <i className="ri-whatsapp-line mr-2"></i>
                WhatsApp
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 rounded-lg ${
                isScrolled ? 'text-gray-700' : 'text-white'
              }`}
              aria-label="Toggle menu"
            >
              <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white border-t"
            >
              <div className="px-4 py-4 space-y-3">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`block px-4 py-2 rounded-lg text-sm font-medium ${
                      location.pathname === link.path
                        ? 'bg-[#ff0000] text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <a
                  href="tel:+34602365996"
                  className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  <i className="ri-phone-line mr-2"></i>
                  602 36 59 96
                </a>
                <a
                  href="https://wa.me/34602365996"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-4 py-2.5 bg-[#ff0000] text-white text-sm font-semibold rounded-lg text-center"
                >
                  <i className="ri-whatsapp-line mr-2"></i>
                  WhatsApp
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Mobile Sticky CTA */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t shadow-lg p-3 flex gap-2">
        <a
          href="tel:+34602365996"
          className="flex-1 px-4 py-3 bg-gray-800 text-white text-sm font-semibold rounded-lg text-center whitespace-nowrap"
        >
          <i className="ri-phone-line mr-2"></i>
          Llamar
        </a>
        <a
          href="https://wa.me/34602365996"
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 px-4 py-3 bg-[#25D366] text-white text-sm font-semibold rounded-lg text-center whitespace-nowrap"
        >
          <i className="ri-whatsapp-line mr-2"></i>
          WhatsApp
        </a>
      </div>

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/34602365996"
        target="_blank"
        rel="noopener noreferrer"
        className="hidden lg:flex fixed bottom-6 right-6 z-40 w-14 h-14 bg-[#25D366] text-white rounded-full items-center justify-center shadow-lg hover:scale-110 transition-transform"
        aria-label="WhatsApp"
      >
        <i className="ri-whatsapp-line text-2xl"></i>
      </a>
    </>
  );
};

export default Navbar;
